import React, { useState } from "react";
import {
	Box,
	Typography,
	TextField,
	Button,
	InputAdornment,
	Link,
	IconButton,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useFirebase } from "../context/ApiContext";
import { useSnackbar } from "notistack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const ResetPasswordPage = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { resetPassword } = useFirebase();
	const { enqueueSnackbar } = useSnackbar();
	const token = searchParams.get("token");

	const [password, setPassword] = useState("");
	const [repeatPassword, setRepeatPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [errors, setErrors] = useState({ password: "", repeatPassword: "" });

	const handleSubmit = async (e) => {
		e.preventDefault();
		const newErrors = { password: "", repeatPassword: "" };
		if (!password) {
			newErrors.password = "La contraseña es obligatoria.";
		} else if (password.length < 6) {
			newErrors.password = "Mínimo 6 caracteres.";
		}
		if (!repeatPassword) {
			newErrors.repeatPassword = "Repite la contraseña.";
		} else if (password !== repeatPassword) {
			newErrors.repeatPassword = "Las contraseñas no coinciden.";
		}
		setErrors(newErrors);
		if (newErrors.password || newErrors.repeatPassword) return;

		setLoading(true);
		try {
			await resetPassword(token, password);
			setSuccess(true);
			enqueueSnackbar("Contraseña actualizada correctamente", {
				variant: "success",
			});
		} catch (err) {
			const msg =
				err?.response?.data?.message ||
				err?.message ||
				"El enlace es inválido o ha expirado.";
			enqueueSnackbar(msg, { variant: "error" });
		} finally {
			setLoading(false);
		}
	};

	if (!token) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
					p: 3,
				}}
			>
				<Box
					sx={{
						maxWidth: 440,
						width: "100%",
						bgcolor: "#fff",
						borderRadius: "24px",
						p: 4,
						boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
						textAlign: "center",
					}}
				>
					<Typography sx={{ fontSize: 16, color: "#64748b", mb: 2 }}>
						Falta el enlace de restablecimiento. Solicita uno nuevo desde
						olvidar contraseña.
					</Typography>
					<Button
						onClick={() => navigate("/forgot-password")}
						sx={{
							background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
							color: "#fff!important",
							textTransform: "none",
							borderRadius: "12px",
						}}
					>
						Olvidé mi contraseña
					</Button>
				</Box>
			</Box>
		);
	}

	if (success) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
					p: 3,
				}}
			>
				<Box
					sx={{
						maxWidth: 440,
						width: "100%",
						bgcolor: "#fff",
						borderRadius: "24px",
						p: 4,
						boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
						textAlign: "center",
					}}
				>
					<Box
						sx={{
							width: 72,
							height: 72,
							borderRadius: "50%",
							background: "linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							mx: "auto",
							mb: 3,
						}}
					>
						<CheckCircleIcon sx={{ fontSize: 40, color: "#059669" }} />
					</Box>
					<Typography
						sx={{
							fontSize: 22,
							fontWeight: 700,
							color: "#1a1a2e",
							mb: 1,
						}}
					>
						Contraseña actualizada
					</Typography>
					<Typography sx={{ fontSize: 14, color: "#64748b", mb: 3 }}>
						Ya puedes iniciar sesión con tu nueva contraseña.
					</Typography>
					<Button
						onClick={() => navigate("/")}
						sx={{
							height: 48,
							borderRadius: "14px",
							background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
							color: "#fff!important",
							textTransform: "none",
							fontWeight: 600,
							px: 4,
							"&:hover": {
								background: "linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
							},
						}}
					>
						Volver{" "}
					</Button>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
				p: 3,
			}}
		>
			<Box
				sx={{
					maxWidth: 440,
					width: "100%",
					bgcolor: "#fff",
					borderRadius: "24px",
					p: 4,
					boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
				}}
			>
				<Link
					component="button"
					onClick={() => navigate("/forgot-password")}
					sx={{
						display: "inline-flex",
						alignItems: "center",
						gap: 0.5,
						mb: 3,
						color: "#64748b",
						fontSize: 14,
						textDecoration: "none",
						"&:hover": { color: "#6366F1" },
						cursor: "pointer",
					}}
				>
					<ArrowBackIcon sx={{ fontSize: 20 }} />
					Volver
				</Link>

				<Typography
					sx={{
						fontSize: 22,
						fontWeight: 700,
						color: "#1a1a2e",
						mb: 0.5,
					}}
				>
					Nueva contraseña
				</Typography>
				<Typography
					sx={{
						fontSize: 14,
						color: "#64748b",
						mb: 3,
						lineHeight: 1.6,
					}}
				>
					Elige una contraseña de al menos 6 caracteres.
				</Typography>

				<form onSubmit={handleSubmit}>
					<TextField
						fullWidth
						label="Nueva contraseña"
						type={showPassword ? "text" : "password"}
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						error={!!errors.password}
						helperText={errors.password}
						placeholder="Mínimo 6 caracteres"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<LockIcon sx={{ color: "text.secondary" }} />
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										onClick={() => setShowPassword((p) => !p)}
										edge="end"
										aria-label="toggle password"
									>
										{showPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
						sx={{
							mb: 2,
							"& .MuiOutlinedInput-root": { borderRadius: "12px" },
						}}
					/>
					<TextField
						fullWidth
						label="Repite la contraseña"
						type={showPassword ? "text" : "password"}
						value={repeatPassword}
						onChange={(e) => setRepeatPassword(e.target.value)}
						error={!!errors.repeatPassword}
						helperText={errors.repeatPassword}
						placeholder="Confirma tu contraseña"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<LockIcon sx={{ color: "text.secondary" }} />
								</InputAdornment>
							),
						}}
						sx={{
							mb: 2,
							"& .MuiOutlinedInput-root": { borderRadius: "12px" },
						}}
					/>
					<Button
						type="submit"
						fullWidth
						disabled={loading}
						sx={{
							height: 48,
							borderRadius: "12px",
							background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
							color: "#fff!important",
							textTransform: "none",
							fontWeight: 600,
							fontSize: 14,
							"&:hover": {
								background: "linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
							},
						}}
					>
						{loading ? "Guardando…" : "Guardar contraseña"}
					</Button>
				</form>
			</Box>
		</Box>
	);
};

export default ResetPasswordPage;
